/**
 * Credential redaction, shared by every path that can send data out of the
 * add-in: our own backend, Sentry, the clipboard, and what we keep in storage.
 *
 * This lives on its own so that sanitising happens ONCE, at the moment an error
 * is captured, rather than separately per destination. Doing it per destination
 * is how the first version leaked: Sentry's scrubber never looked at the
 * exception message, and the backend and clipboard paths had no scrubbing at
 * all.
 *
 * Mirrors frontend-legal's app/utils/redactSensitive.ts.
 */

const SENSITIVE_KEY =
  /^(authorization|cookie|set-cookie|x-auth-token|access[-_]?token|refresh[-_]?token|token|password|secret|ticket|jwt|api[-_]?key)$/i;
const BEARER = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const TOKEN_IN_URL =
  /((?:^|[?&])(?:token|ticket|access_token|refresh_token|pwd|password|api_key)=)[^&#\s]+/gi;
/** "Cookie: session=abc" / "authorization: Bearer x" written into a message. */
const HEADER_IN_TEXT =
  /\b(authorization|cookie|set-cookie|x-auth-token|password|secret|api[-_]?key)\b\s*[:=]\s*\S+/gi;

/** Longest a single captured string may be. Caps how much of a document could
 * ever ride along inside an error message, and bounds what we store and send. */
export const MAX_CAPTURED_STRING = 2000;

/** Strip credentials that appear inside a free-text string. */
export const redactString = (value: string): string => {
  const cleaned = value
    .replace(BEARER, "Bearer <redacted>")
    .replace(HEADER_IN_TEXT, (_m, key: string) => `${key}: <redacted>`)
    .replace(TOKEN_IN_URL, "$1<redacted>")
    .replace(JWT, "<jwt-redacted>");

  return cleaned.length > MAX_CAPTURED_STRING
    ? `${cleaned.slice(0, MAX_CAPTURED_STRING)}… <truncated>`
    : cleaned;
};

/**
 * Redact credential-bearing values anywhere in a structure, in place.
 * Walks arrays as well as objects — credentials hide in arrays too.
 * Depth-limited and cycle-safe: this runs on the way out and must never hang.
 */
export function redactDeep(value: unknown, depth = 0, seen = new WeakSet<object>()): void {
  if (value == null || typeof value !== "object" || depth > 8) return;
  if (seen.has(value as object)) return;
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      const entry: unknown = value[i];
      if (typeof entry === "string") {
        value[i] = redactString(entry);
      } else {
        redactDeep(entry, depth + 1, seen);
      }
    }
    return;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (SENSITIVE_KEY.test(key)) {
      record[key] = "[Filtered]";
      continue;
    }
    const entry: unknown = record[key];
    if (typeof entry === "string") {
      record[key] = redactString(entry);
    } else {
      redactDeep(entry, depth + 1, seen);
    }
  }
}
